import {
    ShoppingBagIcon,
    CurrencyDollarIcon,
    MapPinIcon,
    UserIcon,
    GlobeAltIcon,
} from "@heroicons/react/24/solid";
import { MemoTextJsonTypeEnum } from "@/entities/MemoText/MemoText.model";
import { formattedUrl, money } from "@/utils/misc";
import {
    NAICS_6_DIGIT_CODE,
    NAICS_INDUSTRY_GROUP_OPTIONS,
} from "@/constants/industries.constants";
import clsx from "clsx";
import { US_STATES } from "@/constants/location.constants";
import type { Company } from "@/entities/Company";
import type { SfdcData } from "@/entities/Deal/SFDC.types";
import { useAtomValue } from "jotai";
import { showDataSourcesAtom } from "../state";
import { DataSource, SourceTag } from "./SourceTag";
import { MetricSource } from "@/features/Metrics/Metrics.types";

const InfoDisplay = ({
    value,
    isLoading,
    source,
    showDataSources,
    fieldName,
}: {
    value?: string | number;
    isLoading: boolean;
    source?: MetricSource | DataSource | null;
    showDataSources?: boolean;
    fieldName?: string;
}) => {
    if (isLoading) {
        return (
            <div
                className={clsx(
                    "h-3 w-10 bg-slate-700",
                    "animate-pulse rounded",
                )}
            />
        );
    }

    return (
        <div className="flex flex-col">
            <p className={clsx("body-s subdued")}>{value || "-"}</p>
            {showDataSources && (
                <SourceTag source={source} fieldName={fieldName} />
            )}
        </div>
    );
};

export const MetaInfoBar = ({
    company,
    sfdcData,
    text = {},
    isGenerating,
    isLoadingCompany = false,
    isLoadingSfdc = false,
    hideDataSources = false,
}: {
    company: Company | undefined;
    sfdcData: SfdcData[] | undefined;
    text?: Record<string, any>;
    isGenerating: boolean;
    isLoadingCompany?: boolean;
    isLoadingSfdc?: boolean;
    hideDataSources?: boolean;
}) => {
    const showDataSourcesAtomValue = useAtomValue(showDataSourcesAtom);
    const showDataSources = !hideDataSources && showDataSourcesAtomValue;

    const facilitySize = sfdcData?.[0]?.totalFacilitySize;
    const metadata = text?.[MemoTextJsonTypeEnum.COMPANY_METADATA]?.data;
    const companyState = company?.state ? US_STATES[company?.state] : null;
    const companyCountry = company?.country;
    const locationValue = [companyState, companyCountry]
        .filter(Boolean)
        .join(", ");
    const industry =
        NAICS_6_DIGIT_CODE[metadata?.naicsCode as string] ||
        NAICS_INDUSTRY_GROUP_OPTIONS[
            company?.capitalApplication.selfReportedIndustry as string
        ];
    const stageName = sfdcData?.[0]?.stage;

    return (
        <div className="flex flex-wrap gap-x-6 gap-y-2">
            {stageName && (
                <div className="flex items-center">
                    <div
                        className={clsx(
                            "max-h-[24px] w-fit rounded-full bg-[#93C5FD] px-2 py-1",
                            "flex items-center",
                        )}
                    >
                        <span className="ui-label text-slate-900">
                            {stageName}
                        </span>
                    </div>
                </div>
            )}
            <div className="flex items-center gap-1.5">
                <CurrencyDollarIcon className=" size-4" />
                <InfoDisplay
                    value={facilitySize ? money(facilitySize) : undefined}
                    source={DataSource.SFDC}
                    showDataSources={showDataSources}
                    fieldName="sfdc.totalFacilitySize"
                    isLoading={isLoadingSfdc}
                />
            </div>

            <div className="flex items-center gap-1.5">
                <MapPinIcon className=" size-4" />
                <InfoDisplay
                    value={locationValue}
                    source={DataSource.LEGACY_BACKEND}
                    showDataSources={showDataSources}
                    isLoading={isLoadingCompany}
                    fieldName="company.state, company.country"
                />
            </div>
            <div className="flex items-center gap-1.5">
                <UserIcon className=" size-4" />
                <InfoDisplay
                    value={metadata?.headcountEstimate}
                    source={DataSource.MEMO_TEXT}
                    showDataSources={showDataSources}
                    isLoading={isGenerating}
                    fieldName="memoText.companyMetadata.headcountEstimate"
                />
            </div>
            <div className="flex items-center gap-1.5">
                <GlobeAltIcon className=" size-4" />
                <InfoDisplay
                    value={formattedUrl(company?.website || null)}
                    source={DataSource.LEGACY_BACKEND}
                    showDataSources={showDataSources}
                    isLoading={isLoadingCompany}
                    fieldName="company.website"
                />
            </div>
            <div className="flex items-center gap-1.5">
                <ShoppingBagIcon className=" size-4" />
                <InfoDisplay
                    value={industry}
                    source={
                        metadata?.naicsCode
                            ? DataSource.MEMO_TEXT
                            : DataSource.LEGACY_BACKEND
                    }
                    showDataSources={showDataSources}
                    isLoading={isLoadingCompany || isGenerating}
                    fieldName={
                        metadata?.naicsCode
                            ? "memoText.companyMetadata.naicsCode"
                            : "company.capitalApplication.selfReportedIndustry"
                    }
                />
            </div>
        </div>
    );
};
